import { useState, useRef, useEffect } from "react";
import { useNotifications } from "@/hooks/useNotifications";
import { Bell, BellRing, Check, Trash2, ShoppingBag, WifiOff, Wifi, BellPlus, BellOff } from "lucide-react";

export function NotificationBell() {
  const {
    notifications, unreadCount, connected,
    permissionState, pushSubscribed,
    requestPermission, subscribePush, unsubscribePush,
    markAllRead, clearAll,
  } = useNotifications();
  const [open, setOpen] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const panelRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleOpen = () => {
    setOpen(!open);
    if (permissionState === "default") requestPermission();
  };

  const handlePushToggle = async () => {
    setPushLoading(true);
    try {
      if (pushSubscribed) {
        await unsubscribePush();
      } else {
        await subscribePush();
      }
    } finally {
      setPushLoading(false);
    }
  };

  const timeAgo = (ts) => {
    if (!ts) return "";
    const diff = (Date.now() - new Date(ts).getTime()) / 1000;
    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const showPushBanner = permissionState !== "denied" && "PushManager" in window;

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={handleOpen}
        className="relative p-2 rounded-xl hover:bg-cream-100 transition-colors group"
        data-testid="notification-bell"
      >
        {unreadCount > 0 ? (
          <BellRing className="w-5 h-5 text-[#E08A4E] animate-pulse" />
        ) : (
          <Bell className="w-5 h-5 text-cream-900/40 group-hover:text-cream-900/70 transition-colors" />
        )}
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1 shadow-sm"
            data-testid="notification-badge"
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-12 w-80 sm:w-96 bg-th-card rounded-2xl shadow-2xl border border-cream-200 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200"
          data-testid="notification-panel"
        >
          <div className="px-4 py-3 border-b border-cream-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-cream-900">Notifications</h3>
              <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${connected ? "bg-green-500/10 text-green-500" : "bg-amber-500/10 text-amber-500"}`}>
                {connected ? <Wifi className="w-2.5 h-2.5" /> : <WifiOff className="w-2.5 h-2.5" />}
                {connected ? "Live" : "Reconnecting"}
              </div>
            </div>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button onClick={markAllRead} className="p-1.5 rounded-lg hover:bg-cream-50 text-cream-900/40 hover:text-cream-900/70 transition-colors" title="Mark all read" data-testid="mark-all-read-btn">
                  <Check className="w-4 h-4" />
                </button>
              )}
              {notifications.length > 0 && (
                <button onClick={() => { clearAll(); setOpen(false); }} className="p-1.5 rounded-lg hover:bg-cream-50 text-cream-900/40 hover:text-red-500 transition-colors" title="Clear all" data-testid="clear-all-btn">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Push Notification Banner */}
          {showPushBanner && (
            <div className={`px-4 py-2.5 border-b ${pushSubscribed ? "bg-green-500/10 border-green-500/20" : "bg-clay-500/10 border-clay-500/20"}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {pushSubscribed ? <BellOff className="w-3.5 h-3.5 text-green-500" /> : <BellPlus className="w-3.5 h-3.5 text-clay-500" />}
                  <p className={`text-xs ${pushSubscribed ? "text-green-500" : "text-clay-500"}`}>
                    {pushSubscribed ? "Background alerts active" : "Get alerts even when app is closed"}
                  </p>
                </div>
                <button
                  onClick={handlePushToggle}
                  disabled={pushLoading}
                  className={`text-xs font-semibold px-3 py-1 rounded-full transition-colors ${
                    pushSubscribed
                      ? "text-green-500 bg-green-500/20 hover:bg-green-500/30"
                      : "text-clay-500 bg-clay-500/20 hover:bg-clay-500/30"
                  }`}
                  data-testid="push-toggle-btn"
                >
                  {pushLoading ? "..." : pushSubscribed ? "Disable" : "Enable"}
                </button>
              </div>
            </div>
          )}

          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-12 text-center">
                <Bell className="w-10 h-10 mx-auto mb-3 text-cream-900/15" />
                <p className="text-sm text-cream-900/30">No notifications yet</p>
                <p className="text-xs text-cream-900/20 mt-1">New orders will appear here in real-time</p>
              </div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.id}
                  className={`px-4 py-3 border-b border-cream-50 transition-colors ${n.read ? "bg-th-card" : "bg-amber-500/5"}`}
                  data-testid={`notification-item-${n.id}`}
                >
                  <div className="flex gap-3">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${n.read ? "bg-cream-100" : "bg-[#E08A4E]/10"}`}>
                      <ShoppingBag className={`w-4 h-4 ${n.read ? "text-cream-900/40" : "text-[#E08A4E]"}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${n.read ? "text-cream-900/60" : "text-cream-900 font-medium"}`}>{n.title}</p>
                      <p className="text-xs text-cream-900/50 mt-0.5">{n.body}</p>
                      <p className="text-[10px] text-cream-900/30 mt-1">{timeAgo(n.timestamp)}</p>
                    </div>
                    {!n.read && <div className="w-2 h-2 rounded-full bg-[#E08A4E] flex-shrink-0 mt-2" />}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
